import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ComponentProps, Suspense, lazy, useCallback, useState } from "react";
import {
  BackButtonContext,
  ChangeTitleContext,
  SetBackButtonVisibilityContext,
  ShowUpgradeModalContext,
  ThemeContext,
  TitleContext,
  TitleDescriptionContext,
  ToggleThemeContext,
} from "@common/context/ThemeContext";
import { handleApiError, shouldRetryOnError } from "@common/helpers/api";
import { __ } from "@common/helpers/i18n";
import { NotificationContainer } from "@app/layout/NotificationContainer";
import UserDataProvider from "./UserDataProvider";

const UpgradeModal = lazy(() =>
  import("@app/elements/modals/UpgradeModal").then((module) => ({
    default: module.UpgradeModal,
  })),
);

function hasMutationErrorHandler(mutation: {
  options?: { onError?: unknown };
}) {
  return typeof mutation.options?.onError === "function";
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: shouldRetryOnError,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      handleApiError(
        error,
        query.meta !== undefined && query.meta.hideError === true,
      );
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (hasMutationErrorHandler(mutation)) {
        return;
      }

      handleApiError(
        error,
        mutation.meta !== undefined && mutation.meta.hideError === true,
        { showFallback: true },
      );
    },
  }),
});

export default function Providers({
  children,
  theme,
  toggleTheme,
  installedVersion,
  onInstall,
}: {
  children: React.ReactNode;
  theme: "light" | "dark";
  toggleTheme: () => void;
} & Omit<ComponentProps<typeof UserDataProvider>, "children">) {
  const [title, setTitle] = useState("");
  const [titleDescription, setTitleDescription] = useState<string>();
  const [showBackButton, setShowBackButton] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const changeTitle = useCallback((title: string, description?: string) => {
    setTitle(title);
    setTitleDescription(description);
    if (typeof document !== "undefined") {
      (document as any).title =
        title + " - " + __(import.meta.env.VITE_SITE_NAME);
    }
  }, []);

  const upgradeModal = useCallback(() => setIsUpgradeModalOpen(true), []);

  return (
    <QueryClientProvider client={queryClient}>
      <UserDataProvider
        installedVersion={installedVersion}
        onInstall={onInstall}
      >
        <TitleContext.Provider value={title}>
          <TitleDescriptionContext.Provider value={titleDescription}>
            <ChangeTitleContext.Provider value={changeTitle}>
              <ThemeContext.Provider value={theme}>
                <ToggleThemeContext.Provider value={toggleTheme}>
                  <BackButtonContext.Provider value={showBackButton}>
                    <SetBackButtonVisibilityContext.Provider
                      value={setShowBackButton}
                    >
                      <ShowUpgradeModalContext.Provider value={upgradeModal}>
                        {isUpgradeModalOpen && (
                          <Suspense fallback={null}>
                            <UpgradeModal
                              isOpen={isUpgradeModalOpen}
                              onClose={() => setIsUpgradeModalOpen(false)}
                              showCloseIcon
                            />
                          </Suspense>
                        )}
                        {children}
                        <NotificationContainer />
                      </ShowUpgradeModalContext.Provider>
                    </SetBackButtonVisibilityContext.Provider>
                  </BackButtonContext.Provider>
                </ToggleThemeContext.Provider>
              </ThemeContext.Provider>
            </ChangeTitleContext.Provider>
          </TitleDescriptionContext.Provider>
        </TitleContext.Provider>
      </UserDataProvider>
    </QueryClientProvider>
  );
}
